import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import * as api from '../../services/api';

const roleStyles: Record<string, string> = {
  Admin: 'bg-anchor-blue/10 text-anchor-blue border-anchor-blue/20',
  Analyst: 'bg-anchor-purple/10 text-anchor-purple border-anchor-purple/20',
  Viewer: 'bg-white/[0.04] text-white/50 border-white/10',
};

const roleDescriptions = [
  { role: 'Admin', desc: 'Full access to all settings, user management, and agent configuration' },
  { role: 'Analyst', desc: 'View dashboards, audit results, and compliance reports. Cannot modify settings.' },
  { role: 'Viewer', desc: 'Read-only access to dashboards and high-level metrics' },
];

const UserSettings: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Viewer');
  const [inviting, setInviting] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [inviteError, setInviteError] = useState('');
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);
  const [roleUpdateStatus, setRoleUpdateStatus] = useState<{ id: string; status: 'success' | 'error'; message: string } | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!user?.tenantId) { setLoading(false); return; }
    try {
      const data = await api.getUsers(user.tenantId);
      setUsers(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
      // Show current user as fallback
      setUsers([{ id: '0', name: user.name, email: user.email, role: 'Admin', lastLogin: 'Current session' }]);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, user?.name, user?.email]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleInvite = async () => {
    if (!inviteEmail || !user?.tenantId) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      setInviteStatus('error');
      setInviteError('Please enter a valid email address');
      return;
    }

    setInviting(true);
    setInviteStatus('idle');
    try {
      await api.inviteUser(user.tenantId, inviteEmail, inviteRole);
      setInviteStatus('success');
      setInviteEmail('');
      setInviteRole('Viewer');
      showToast('Invite sent successfully', 'success');
      fetchUsers(); // Refresh list
    } catch (err) {
      setInviteStatus('error');
      setInviteError(err instanceof Error ? err.message : 'Failed to send invite');
      showToast('Failed to send invite', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!user?.tenantId) return;
    setUpdatingRoleId(userId);
    setRoleUpdateStatus(null);
    try {
      const updated = await api.updateUserRole(user.tenantId, userId, newRole);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: updated.role || newRole } : u));
      setRoleUpdateStatus({ id: userId, status: 'success', message: 'Role updated' });
      setTimeout(() => setRoleUpdateStatus(prev => prev?.id === userId ? null : prev), 2000);
    } catch (err) {
      setRoleUpdateStatus({ id: userId, status: 'error', message: err instanceof Error ? err.message : 'Failed to update role' });
    } finally {
      setUpdatingRoleId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-medium text-white">Team Members</h3>
            <p className="text-sm text-white/40 mt-0.5">{users.length} user{users.length !== 1 ? 's' : ''} in your organization</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue"></div>
          </div>
        ) : error ? (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 text-xs text-amber-300 mb-4">
            {error} — showing available data
          </div>
        ) : null}

        <div className="space-y-2">
          {users.map((u: any) => (
            <div key={u.id} className="flex items-center justify-between py-3 px-4 bg-white/[0.01] border border-white/[0.03] rounded-lg hover:bg-white/[0.02] transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-anchor-blue/20 flex items-center justify-center text-xs font-medium text-anchor-blue">
                  {(u.name || u.email || '?').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <div className="text-sm text-white/80">{u.name || u.email}</div>
                  <div className="text-xs text-white/40 font-mono">{u.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {u.id === user?.id ? (
                  <span className={`text-[10px] py-0.5 px-2 rounded-full border ${roleStyles[u.role] || roleStyles.Viewer}`}>
                    {u.role || 'Viewer'} (you)
                  </span>
                ) : (
                  <>
                    <select
                      value={u.role || 'Viewer'}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      disabled={updatingRoleId === u.id}
                      className={`text-[11px] py-0.5 px-2 rounded-lg border bg-white/[0.02] focus:outline-none focus:border-anchor-blue/50 transition-colors cursor-pointer disabled:opacity-50 ${roleStyles[u.role] || roleStyles.Viewer}`}
                    >
                      <option value="Admin">Admin</option>
                      <option value="Analyst">Analyst</option>
                      <option value="Viewer">Viewer</option>
                    </select>
                    {roleUpdateStatus && roleUpdateStatus.id === u.id && (
                      <span className={`text-[10px] ${roleUpdateStatus.status === 'success' ? 'text-anchor-teal' : 'text-red-400'}`}>
                        {roleUpdateStatus.message}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invite User */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-4">Invite User</h3>
        <div className="flex gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => { setInviteEmail(e.target.value); setInviteStatus('idle'); }}
            placeholder="Enter email address"
            className="flex-1 py-2.5 px-4 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50 transition-colors"
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="py-2.5 px-4 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white/70 focus:outline-none focus:border-anchor-blue/50 transition-colors"
          >
            <option value="Viewer">Viewer</option>
            <option value="Analyst">Analyst</option>
            <option value="Admin">Admin</option>
          </select>
          <button
            onClick={handleInvite}
            disabled={inviting || !inviteEmail}
            className="py-2.5 px-5 bg-anchor-blue border-none rounded-lg text-white text-sm font-medium cursor-pointer hover:bg-[#3636DD] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {inviting ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
        {inviteStatus === 'success' && (
          <p className="mt-2 text-xs text-anchor-teal">Invite sent successfully!</p>
        )}
        {inviteStatus === 'error' && (
          <p className="mt-2 text-xs text-red-400">{inviteError}</p>
        )}
      </div>

      {/* Role Descriptions */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-4">Role Permissions</h3>
        <div className="space-y-3">
          {roleDescriptions.map((r) => (
            <div key={r.role} className="flex items-start gap-3 py-2">
              <span className={`text-[10px] py-0.5 px-2 rounded-full border mt-0.5 ${roleStyles[r.role]}`}>
                {r.role}
              </span>
              <p className="text-sm text-white/50">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
